import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Loader2,
  Package,
  Mail,
  FileText,
  ExternalLink,
  CheckCircle2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Quote History — read-only list of all quote requests the user has submitted.
 *
 * - Distributor: their own requests
 * - Agency user: all requests routed to their agency
 * - Admin: all requests
 *
 * Each request snapshots the manufacturer, line items, and pricing contacts
 * at the moment of submission so the history is stable.
 */
export default function QuoteHistoryPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/quote-requests");
        if (!cancelled) setRequests(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setRequests([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDelete = async (req) => {
    const label = req.manufacturer_name || "this quote request";
    if (!window.confirm(`Delete the quote request for ${label}? This cannot be undone.`)) {
      return;
    }
    setDeletingId(req.id);
    try {
      await api.delete(`/quote-requests/${req.id}`);
      setRequests((prev) => prev.filter((r) => r.id !== req.id));
      toast.success("Quote request deleted");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not delete this quote request"
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
          Activity
        </div>
        <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
          Quote history
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every quote request you've submitted. Each row is a snapshot of what
          you sent — items, contacts, and the message at the time of submit.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
          Loading…
        </div>
      ) : requests.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4" data-testid="quote-history-list">
          {requests.map((r) => (
            <QuoteRequestCard
              key={r.id}
              req={r}
              onDelete={handleDelete}
              isDeleting={deletingId === r.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-border rounded-sm p-12 text-center text-muted-foreground">
      <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.25} />
      <div className="font-medium text-slate-700">No quote requests yet.</div>
      <div className="text-xs mt-1">
        Add products to your cart from the Search screen, then hit "Request
        quote."
      </div>
    </div>
  );
}

function QuoteRequestCard({ req, onDelete, isDeleting }) {
  const created = req.created_at
    ? new Date(req.created_at).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";
  const itemCount = (req.items || []).reduce(
    (sum, it) => sum + (it.quantity || 0),
    0
  );

  return (
    <article
      className="bg-white border border-border rounded-sm overflow-hidden"
      data-testid={`quote-request-${req.id}`}
    >
      <header className="px-5 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-slate-50">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-mono-data font-semibold">
              {req.status || "submitted"}
            </span>
          </div>
          <h2 className="mt-1 font-display text-base font-bold tracking-tight text-slate-900 truncate">
            {req.manufacturer_name || "—"}
          </h2>
          <div className="text-xs text-muted-foreground font-mono-data">{created}</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
              Items
            </div>
            <div className="font-display text-lg font-bold text-slate-900">{itemCount}</div>
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(req)}
              disabled={isDeleting}
              className="w-8 h-8 rounded-sm border border-border bg-white hover:bg-red-50 hover:border-red-300 hover:text-red-600 text-slate-500 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Delete quote request"
              data-testid={`delete-quote-${req.id}`}
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </header>

      <ul className="divide-y divide-border">
        {(req.items || []).map((it, idx) => (
          <li
            key={idx}
            className="px-5 py-3 flex items-start justify-between gap-3"
            data-testid={`quote-line-${req.id}-${idx}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm text-slate-900 truncate">
                {it.product_name}
              </div>
              {it.model_number && (
                <div className="text-[11px] font-mono-data text-slate-700 truncate">
                  {it.model_number}
                </div>
              )}
              {it.note && (
                <div className="mt-1 text-xs text-muted-foreground italic">
                  Note: {it.note}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {it.spec_sheet_url && (
                <a
                  href={
                    it.spec_sheet_url.startsWith("http")
                      ? it.spec_sheet_url
                      : `${BACKEND_URL}${it.spec_sheet_url}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-amber-600 underline underline-offset-2"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Spec
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
                  Qty
                </div>
                <div className="font-display font-bold text-slate-900">
                  {it.quantity}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {(req.customer_message || (req.agency_pricing_emails && req.agency_pricing_emails.length > 0)) && (
        <footer className="px-5 py-3 bg-slate-50 border-t border-border space-y-2">
          {req.customer_message && (
            <div className="text-xs text-slate-700">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data mr-2">
                Your message:
              </span>
              {req.customer_message}
            </div>
          )}
          {req.agency_pricing_emails && req.agency_pricing_emails.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Mail className="w-3.5 h-3.5" />
              <span>Routed to {req.agency_name || "your rep agency"}:</span>
              <span className="font-mono-data text-slate-700">
                {req.agency_pricing_emails.join(", ")}
              </span>
            </div>
          )}
        </footer>
      )}
    </article>
  );
}
